import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  application: { id: string; name: string };
}

interface PreviewItem {
  description: string;
  amountCents: number;
  ledgerAccount: string;
}

interface PreviewTenant {
  externalTenantId: string;
  sourceTenantId: string;
  displayName: string;
  billingEmail: string | null;
  platformInvoiceCount: number;
  subtotalCents: number;
  totalCents: number;
  items: PreviewItem[];
  blockingIssue: string | null;
}

interface PreviewResult {
  applicationId: string;
  applicationName: string;
  billingMonth: string;
  tenantCount: number;
  totalCents: number;
  tenants: PreviewTenant[];
}

interface GenerateOutcome {
  externalTenantId: string;
  sourceTenantId: string;
  displayName: string;
  status: 'created' | 'reused' | 'skipped' | 'failed';
  bizInvoiceId: string | null;
  stripeInvoiceId: string | null;
  stripeHostedUrl: string | null;
  totalCents: number;
  reason?: string;
}

interface GenerateResult {
  applicationId: string;
  applicationName: string;
  billingMonth: string;
  attempted: number;
  created: number;
  reused: number;
  skipped: number;
  failed: number;
  totalCents: number;
  tenants: GenerateOutcome[];
}

function fmtMoney(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function lastNMonths(n: number): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - i - 1, 1));
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    out.push({ value, label });
  }
  return out;
}

function statusBadge(status: GenerateOutcome['status']) {
  switch (status) {
    case 'created':
      return <Badge className="bg-green-100 text-green-800">Created</Badge>;
    case 'reused':
      return <Badge variant="secondary">Already sent</Badge>;
    case 'skipped':
      return <Badge variant="outline" className="text-amber-700 border-amber-300">Skipped</Badge>;
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>;
  }
}

export function GenerateInvoicesDialog({ open, onOpenChange, application }: Props) {
  const monthOptions = useMemo(() => lastNMonths(6), []);
  const [billingMonth, setBillingMonth] = useState(monthOptions[0]?.value ?? '');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [result, setResult] = useState<GenerateResult | null>(null);

  const previewMutation = useMutation({
    mutationFn: () =>
      api.previewMyTowMateGeneration({ applicationId: application.id, billingMonth }),
    onSuccess: (resp) => {
      setPreview((resp as { data: PreviewResult }).data);
      setResult(null);
    },
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      api.generateMyTowMateInvoices({ applicationId: application.id, billingMonth }),
    onSuccess: (resp) => {
      setResult((resp as { data: GenerateResult }).data);
    },
  });

  function reset() {
    setPreview(null);
    setResult(null);
    previewMutation.reset();
    generateMutation.reset();
  }

  function handleClose(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  const billable = preview?.tenants.filter((t) => !t.blockingIssue) ?? [];
  const blocked = preview?.tenants.filter((t) => t.blockingIssue) ?? [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate {application.name} invoices</DialogTitle>
          <DialogDescription>
            Roll up open and overdue platform invoices for the chosen billing month into Stripe-collected invoices. Each tenant
            gets one consolidated invoice. Re-running for the same month is safe — already-generated invoices are reused.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="billing-month">Billing month</Label>
            <Select value={billingMonth} onValueChange={(v) => { setBillingMonth(v); reset(); }}>
              <SelectTrigger id="billing-month">
                <SelectValue placeholder="Select a month" />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {previewMutation.isError && (
            <ErrorBox error={previewMutation.error} />
          )}
          {generateMutation.isError && (
            <ErrorBox error={generateMutation.error} />
          )}

          {!result && preview && (
            <div className="space-y-3">
              <PreviewSummary preview={preview} />
              {blocked.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium text-amber-900">
                    <AlertCircle className="h-4 w-4" />
                    {blocked.length} tenant{blocked.length === 1 ? '' : 's'} will be skipped
                  </div>
                  <ul className="mt-2 text-amber-800 space-y-1">
                    {blocked.map((t) => (
                      <li key={t.externalTenantId}>
                        {t.displayName} — {t.blockingIssue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <PreviewTable tenants={billable} />
            </div>
          )}

          {result && (
            <div className="space-y-3">
              <ResultSummary result={result} />
              <ResultTable outcomes={result.tenants} />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleClose(false)}>
            Close
          </Button>
          {!preview && !result && (
            <Button
              onClick={() => previewMutation.mutate()}
              disabled={!billingMonth || previewMutation.isPending}
            >
              {previewMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Preview
            </Button>
          )}
          {preview && !result && (
            <>
              <Button variant="outline" onClick={reset}>Change month</Button>
              <Button
                onClick={() => generateMutation.mutate()}
                disabled={billable.length === 0 || generateMutation.isPending}
              >
                {generateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Generate &amp; send {billable.length} invoice{billable.length === 1 ? '' : 's'}
              </Button>
            </>
          )}
          {result && (
            <Button onClick={reset}>Run again</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewSummary({ preview }: { preview: PreviewResult }) {
  return (
    <div className="grid grid-cols-3 gap-3 rounded-md border bg-muted/40 p-3 text-sm">
      <div><span className="block text-muted-foreground">Tenants</span><span className="font-medium">{preview.tenantCount}</span></div>
      <div><span className="block text-muted-foreground">Billing month</span><span className="font-medium">{preview.billingMonth}</span></div>
      <div><span className="block text-muted-foreground">Total to be invoiced</span><span className="font-medium">{fmtMoney(preview.totalCents)}</span></div>
    </div>
  );
}

function PreviewTable({ tenants }: { tenants: PreviewTenant[] }) {
  if (tenants.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        Nothing to invoice for this month.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b text-left text-muted-foreground">
          <tr>
            <th className="py-2 pr-4">Tenant</th>
            <th className="py-2 pr-4">Lines</th>
            <th className="py-2 pr-4">Email</th>
            <th className="py-2 pr-4 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr key={t.externalTenantId} className="border-b align-top">
              <td className="py-2 pr-4">
                <div className="font-medium">{t.displayName}</div>
                <div className="text-xs text-muted-foreground">tenant_id {t.sourceTenantId}</div>
              </td>
              <td className="py-2 pr-4">
                <ul className="space-y-0.5">
                  {t.items.map((item, i) => (
                    <li key={i} className="text-xs">
                      {item.description} · <span className="text-muted-foreground">{fmtMoney(item.amountCents)}</span>
                    </li>
                  ))}
                </ul>
              </td>
              <td className="py-2 pr-4 text-xs">{t.billingEmail ?? '—'}</td>
              <td className="py-2 pr-4 text-right font-medium">{fmtMoney(t.totalCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultSummary({ result }: { result: GenerateResult }) {
  return (
    <div className="grid grid-cols-5 gap-3 rounded-md border bg-muted/40 p-3 text-sm">
      <div><span className="block text-muted-foreground">Attempted</span><span className="font-medium">{result.attempted}</span></div>
      <div><span className="block text-muted-foreground">Created</span><span className="font-medium text-green-700">{result.created}</span></div>
      <div><span className="block text-muted-foreground">Reused</span><span className="font-medium">{result.reused}</span></div>
      <div><span className="block text-muted-foreground">Skipped</span><span className="font-medium text-amber-700">{result.skipped}</span></div>
      <div><span className="block text-muted-foreground">Failed</span><span className={`font-medium ${result.failed > 0 ? 'text-red-700' : ''}`}>{result.failed}</span></div>
    </div>
  );
}

function ResultTable({ outcomes }: { outcomes: GenerateOutcome[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b text-left text-muted-foreground">
          <tr>
            <th className="py-2 pr-4">Tenant</th>
            <th className="py-2 pr-4">Status</th>
            <th className="py-2 pr-4 text-right">Amount</th>
            <th className="py-2 pr-4">Detail</th>
          </tr>
        </thead>
        <tbody>
          {outcomes.map((o) => (
            <tr key={o.externalTenantId} className="border-b align-top">
              <td className="py-2 pr-4">
                <div className="font-medium">{o.displayName}</div>
                <div className="text-xs text-muted-foreground">tenant_id {o.sourceTenantId}</div>
              </td>
              <td className="py-2 pr-4">{statusBadge(o.status)}</td>
              <td className="py-2 pr-4 text-right">{fmtMoney(o.totalCents)}</td>
              <td className="py-2 pr-4 text-xs">
                {o.status === 'created' || o.status === 'reused' ? (
                  o.stripeHostedUrl ? (
                    <a className="inline-flex items-center gap-1 text-blue-700 hover:underline" href={o.stripeHostedUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-3 w-3" /> hosted invoice
                    </a>
                  ) : (
                    <span className="text-muted-foreground inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> {o.stripeInvoiceId ?? 'sent'}
                    </span>
                  )
                ) : (
                  <span className="text-muted-foreground">{o.reason ?? '—'}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Unknown error';
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
      <div className="flex items-center gap-2 font-medium"><AlertCircle className="h-4 w-4" /> Request failed</div>
      <div className="mt-1 text-xs">{message}</div>
    </div>
  );
}
